/**
 * useTourSandbox
 *
 * Detects the current module from the URL pathname, loads the active demo
 * session for that module, and exposes sandbox activation / deactivation
 * controls. Used by the module-aware sandbox banner and the universal tour.
 */

'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import type { DemoSessionInfo } from '@/lib/demo/types';
import {
  resolveDemoModuleKeyFromPathname,
  fetchDemoSessionResponse,
  enterDemoSession,
  exitDemoSession,
  OPERATIONS_DEMO_SESSION_UPDATED_EVENT,
  OPERATIONS_DEMO_EXIT_REQUESTED_EVENT,
} from '@/lib/demo/clientContext';

export interface UseTourSandboxOptions {
  moduleKey?: string | null;
  autoActivate?: boolean;
}

export interface UseTourSandboxReturn {
  moduleKey: string | null;
  demoSession: DemoSessionInfo | null;
  isSandboxActive: boolean;
  loading: boolean;
  error: string | null;
  activate: () => Promise<void>;
  deactivate: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useTourSandbox(options: UseTourSandboxOptions = {}): UseTourSandboxReturn {
  const pathname = usePathname();
  const detectedModuleKey = useMemo(() => {
    if (options.moduleKey) {
      return options.moduleKey;
    }
    return resolveDemoModuleKeyFromPathname(pathname);
  }, [options.moduleKey, pathname]);

  const [demoSession, setDemoSession] = useState<DemoSessionInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoActivatedRef = useRef<Set<string>>(new Set());

  const isSandboxActive = useMemo(() => {
    if (!detectedModuleKey || !demoSession) {
      return false;
    }
    return Boolean(demoSession.is_demo_mode && demoSession.module_key === detectedModuleKey);
  }, [detectedModuleKey, demoSession]);

  const refresh = useCallback(async () => {
    if (!detectedModuleKey) {
      setDemoSession(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await fetchDemoSessionResponse(detectedModuleKey, { force: true });
      if (result.ok && result.data.success && result.data.session) {
        setDemoSession(result.data.session);
      } else {
        setDemoSession(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sandbox session');
      setDemoSession(null);
    } finally {
      setLoading(false);
    }
  }, [detectedModuleKey]);

  const activate = useCallback(async () => {
    if (!detectedModuleKey) {
      setError('No tour module detected for the current route.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await enterDemoSession(detectedModuleKey);
      if (!result.success || !result.session) {
        throw new Error('Sandbox activation failed');
      }
      setDemoSession(result.session);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enter sandbox');
    } finally {
      setLoading(false);
    }
  }, [detectedModuleKey]);

  const deactivate = useCallback(async () => {
    if (!demoSession?.lease_id) {
      setDemoSession(null);
      return;
    }

    // If the Header is mounted and listening for exit requests, delegate the
    // full exit flow (API call + page reload + state cleanup) to it so the
    // training-mode banner in the Header stays in sync. The Header's handler
    // calls /api/v1/demo/exit, clears demo context, and does a page reload.
    if (typeof window !== 'undefined') {
      setLoading(true);
      window.dispatchEvent(new CustomEvent(OPERATIONS_DEMO_EXIT_REQUESTED_EVENT));
      // Keep loading true — the Header will trigger a page reload.
      // If no Header listener picks this up within 3s, fall back to direct exit.
      setTimeout(() => {
        setLoading(false);
      }, 3000);
      return;
    }

    // Fallback: direct exit when there is no Header listener (e.g. standalone
    // sandbox pages without the Header component).
    setLoading(true);
    setError(null);

    try {
      await exitDemoSession(demoSession.lease_id);
      setDemoSession(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to exit sandbox');
    } finally {
      setLoading(false);
    }
  }, [demoSession?.lease_id]);

  useEffect(() => {
    if (!detectedModuleKey) {
      setDemoSession(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const result = await fetchDemoSessionResponse(detectedModuleKey);
        if (cancelled) return;
        if (result.ok && result.data.success && result.data.session) {
          setDemoSession(result.data.session);
        } else {
          setDemoSession(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load sandbox session');
          setDemoSession(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    const handleUpdate = () => load();
    window.addEventListener(OPERATIONS_DEMO_SESSION_UPDATED_EVENT, handleUpdate);

    return () => {
      cancelled = true;
      window.removeEventListener(OPERATIONS_DEMO_SESSION_UPDATED_EVENT, handleUpdate);
    };
  }, [detectedModuleKey]);

  useEffect(() => {
    if (!options.autoActivate || !detectedModuleKey || isSandboxActive) {
      return;
    }

    if (autoActivatedRef.current.has(detectedModuleKey)) {
      return;
    }

    autoActivatedRef.current.add(detectedModuleKey);

    const timer = setTimeout(() => {
      void activate();
    }, 300);

    return () => clearTimeout(timer);
  }, [options.autoActivate, detectedModuleKey, isSandboxActive, activate]);

  return {
    moduleKey: detectedModuleKey,
    demoSession,
    isSandboxActive,
    loading,
    error,
    activate,
    deactivate,
    refresh,
  };
}
